import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Paper,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { createProject, downloadTemplate } from '../../services/projectService';
import { getAssistants } from '../../services/assistantService';

export default function CreateProjectTab({ onProjectCreated }) {
  const [formData, setFormData] = useState({
    sectionName: '',
    projectName: '',
    projectType: 'data_capture',
    assistantId: '',
    activationStartDate: '',
    activationEndDate: '',
    timezone: 'America/New_York',
  });
  const [file, setFile] = useState(null);
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadAssistants();
  }, []);

  const loadAssistants = async () => {
    try {
      const data = await getAssistants();
      setAssistants(data);
    } catch (err) {
      console.error('Error loading assistants:', err);
      setError('Failed to load assistants');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Only CSV files are allowed');
        return;
      }
      if (selectedFile.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate(formData.projectType);
    } catch (err) {
      console.error('Error downloading template:', err);
      setError('Failed to download template');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Validation
      if (!formData.sectionName || !formData.projectName || !formData.assistantId) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      if (formData.projectType === 'knowledge_base') {
        if (!formData.activationStartDate || !formData.activationEndDate || !formData.timezone) {
          setError('Please fill in activation dates and timezone');
          setLoading(false);
          return;
        }

        // Send as JSON for Knowledge Base
        await createProject('knowledge_base', {
          sectionName: formData.sectionName,
          projectName: formData.projectName,
          projectType: formData.projectType,
          assistantId: formData.assistantId,
          activationStartDate: formData.activationStartDate,
          activationEndDate: formData.activationEndDate,
          timezone: formData.timezone,
        });
      } else {
        // Data Capture or Appointment Reminders
        if (!file) {
          setError('Please select a CSV file');
          setLoading(false);
          return;
        }

        // Send as FormData
        const data = new FormData();
        data.append('sectionName', formData.sectionName);
        data.append('projectName', formData.projectName);
        data.append('projectType', formData.projectType);
        data.append('assistantId', formData.assistantId);
        data.append('file', file);

        await createProject(formData.projectType, data);
      }

      setSuccess(true);
      // Reset form
      setFormData({
        sectionName: '',
        projectName: '',
        projectType: 'data_capture',
        assistantId: '',
        activationStartDate: '',
        activationEndDate: '',
        timezone: 'America/New_York',
      });
      setFile(null);

      if (onProjectCreated) {
        onProjectCreated();
      }
    } catch (err) {
      console.error('Error creating project:', err);
      setError(err.response?.data?.error || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const renderFields = () => {
    if (formData.projectType === 'knowledge_base') {
      return (
        <>
          <TextField
            fullWidth
            label="Activation Start Date"
            name="activationStartDate"
            type="date"
            value={formData.activationStartDate}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Activation End Date"
            name="activationEndDate"
            type="date"
            value={formData.activationEndDate}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            select
            label="Timezone"
            name="timezone"
            value={formData.timezone}
            onChange={handleChange}
            sx={{ mb: 2 }}
          >
            <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
            <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
            <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
            <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
            <MenuItem value="UTC">UTC</MenuItem>
          </TextField>
        </>
      );
    }

    return (
      <>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            onClick={handleDownloadTemplate}
            sx={{ mb: 1 }}
          >
            Download CSV Template
          </Button>
          <Typography variant="caption" display="block" color="text.secondary">
            Download the template for {formData.projectType === 'data_capture' ? 'Data Capture' : 'Appointment Reminders'}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Button variant="contained" component="label">
            {file ? 'Change File' : 'Upload CSV File'}
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleFileChange}
            />
          </Button>
          {file && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
            </Typography>
          )}
        </Box>
      </>
    );
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Create New Project
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Project created successfully!</Alert>}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Section Name"
          name="sectionName"
          value={formData.sectionName}
          onChange={handleChange}
          required
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Project Name"
          name="projectName"
          value={formData.projectName}
          onChange={handleChange}
          required
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          select
          label="Project Type"
          name="projectType"
          value={formData.projectType}
          onChange={handleChange}
          sx={{ mb: 2 }}
        >
          <MenuItem value="data_capture">Data Capture</MenuItem>
          <MenuItem value="appointment_reminders">Appointment Reminders</MenuItem>
          <MenuItem value="knowledge_base">Knowledge Base</MenuItem>
        </TextField>

        <TextField
          fullWidth
          select
          label="Assistant"
          name="assistantId"
          value={formData.assistantId}
          onChange={handleChange}
          required
          sx={{ mb: 2 }}
        >
          <MenuItem value="">Select an assistant</MenuItem>
          {assistants.map((assistant) => (
            <MenuItem key={assistant.assistantId} value={assistant.assistantId}>
              {assistant.name}
            </MenuItem>
          ))}
        </TextField>

        {renderFields()}

        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          fullWidth
          sx={{ mt: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Project'}
        </Button>
      </Box>
    </Paper>
  );
}
